import { useMutation } from '@apollo/client';
import { isValidSerialNumber } from '@barkinglabs/nano-shared';
import classNames from 'classnames';
import { useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import qrCardImage from '../../../assets/images/qr-card.png';
import Button from '../../../components/Button/Button';
import SingleTextFieldForm from '../../../components/form/SingleTextFieldForm';
import { getChipDetails } from '../../../lib/chipsApi';
import { NanoStepFailureReason, trackNanoStepFailed, trackNanoStepSubmitted } from '../../../lib/analytics/events';
import { AppState } from '../../../lib/store';
import { gqlTypes } from '../../../types/gqlTypes';
import { chipRegistrationAddExistingPetMutation } from '../RegisterChip.graphql';
import RegisterChipContextV2 from '../RegisterChipContextV2';
import styles from './SerialNumberStepV2.module.scss';
import StepProgress from '../StepProgress/StepProgress';
import { useScrollToStepTop } from '../useScrollToStepTop';

const InvalidSerialNumberErrorMessage = 'Invalid Serial Number';
const UnknownErrorMessage = 'An unknown error occurred';

const SHORT_CODE_REGEXP = /^[0-9a-zA-Z]{6}$/;
function isValidShortCode(input: string) {
  return SHORT_CODE_REGEXP.test(input);
}

/**
 * Maps a thrown error back to a stable reason for `Nano Registration > Chip > Failed`. Both the
 * lookup and the follow-on registration mutations land in the same catch, so anything that isn't
 * one of the two known lookup messages is a failure to attach the chip to the account.
 */
function chipFailureReason(message: string | undefined): NanoStepFailureReason {
  if (message === InvalidSerialNumberErrorMessage) {
    return 'not_found';
  }
  if (message === UnknownErrorMessage) {
    return 'service_error';
  }
  return 'account_error';
}

function useSubmitSerialNumber() {
  const session = useSelector((state: AppState) => state.session);
  const { registerChipAddEmail, onStepFinished, registerState, setChipDetails, setChipId } =
    useContext(RegisterChipContextV2);

  const [registerChipAddExistingPet] = useMutation<
    gqlTypes.NANO_chipRegistrationAddExistingPet,
    gqlTypes.NANO_chipRegistrationAddExistingPetVariables
  >(chipRegistrationAddExistingPetMutation);

  const [error, setError] = useState<any>();
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    (input: string) => {
      const sanitizedInput = input.trim();
      const validShortCode = isValidShortCode(sanitizedInput);
      const validSerial = isValidSerialNumber(sanitizedInput);

      // `codeType` separates the two ways in: the 6-digit code from the yellow QR card, or a full
      // microchip number. They are different lengths and different error surfaces, and we cannot
      // currently tell which one users are struggling with.
      trackNanoStepSubmitted('chip', {
        codeType: validShortCode ? 'short_code' : validSerial ? 'serial_number' : 'unrecognized',
        length: sanitizedInput.length,
        prefilled: sanitizedInput === registerState.chipId,
      });

      if (!validSerial && !validShortCode) {
        // Rejected client-side, before any network call.
        trackNanoStepFailed('chip', 'invalid_format', { length: sanitizedInput.length });
        setError(InvalidSerialNumberErrorMessage);
        return;
      }

      // Manually set the loading to true
      setLoading(true);
      setChipId(sanitizedInput);

      getChipDetails(sanitizedInput)
        .then(async (chipDetails) => {
          setChipDetails(chipDetails);

          // If we're already logged in, we don't need to ask for them to login so we can go ahead and
          // submit the mutation to register the chip to the user's email
          if (session) {
            await registerChipAddEmail(chipDetails.shortId);
          }

          return chipDetails;
        })
        .catch(async (err: any) => {
          if (err.response?.status === 404) {
            // We don't have this chip in our database. If it's a valid microchip serial, treat it as a
            // new third-party chip: DB membership — not the serial range — decides what's a Fi chip, since
            // the Fi ranges share numeric space with other manufacturers. We set the chip details and
            // create the chip via a GQL mutation after they're registered.
            if (isValidSerialNumber(sanitizedInput)) {
              const chipDetails = {
                serialNumber: sanitizedInput,
                // For 3rd party chips, the shortId is the same as the serial number
                shortId: sanitizedInput,
                // If the chip was not found, then it is not assigned
                isAssigned: false,
              };
              setChipDetails(chipDetails);

              // If we're already signed in, we'll go ahead and register the chip to the user's email
              if (session) {
                await registerChipAddEmail(sanitizedInput);
              }

              // Otherwise we need to move to next step
              return chipDetails;
            } else {
              throw new Error(InvalidSerialNumberErrorMessage);
            }
          } else {
            throw new Error(UnknownErrorMessage);
          }
        })
        .then((chipDetails) => {
          // If petId is in the register state, they started registration by selecting a specific pet they
          // wanted to register the chip to. When this is the case, we'll go ahead and register the chip to that pet
          // after they've submitted the serial number.
          if (registerState.petId) {
            return registerChipAddExistingPet({
              variables: {
                input: {
                  chipId: chipDetails.shortId,
                  petId: registerState.petId,
                },
              },
            });
          }
        })
        .then(() => {
          setLoading(false);
          onStepFinished('chip');
        })
        .catch((err) => {
          trackNanoStepFailed('chip', chipFailureReason(err?.message));
          setError(err.message);
          setLoading(false);
        });
    },
    [
      onStepFinished,
      registerChipAddEmail,
      registerChipAddExistingPet,
      registerState.chipId,
      registerState.petId,
      session,
      setChipDetails,
      setChipId,
    ],
  );

  return {
    handleSubmit,
    error,
    loading,
  };
}

function SerialNumberForm() {
  const {
    registerState: { chipId: registeringChipId },
  } = useContext(RegisterChipContextV2);

  const { handleSubmit, error, loading } = useSubmitSerialNumber();

  return (
    <>
      <SingleTextFieldForm
        fieldName="serialNumber"
        fieldPlaceholder="ex: ABC123"
        errorResponse={error}
        initialValues={{ serialNumber: registeringChipId ?? '' }}
        onSubmit={({ serialNumber }) => {
          handleSubmit(serialNumber);
        }}
        submitButton={<Button primary type="submit" label="Continue" disabled={loading} />}
      />
    </>
  );
}

export default function SerialNumberStep() {
  useScrollToStepTop();

  return (
    <div className={classNames('serial-number-step', styles.container)}>
      <div className={styles.mainContent}>
        <StepProgress step={1} />
        <div className={styles.titleContainer}>
          <div className={styles.title}>Register your pet's microchip</div>
          <div className={styles.subtitle}>
            Takes about two minutes. Registration is what makes the chip useful - an unregistered chip has no contact
            details attached to it.
          </div>
        </div>
        <img alt="Fi QR Card" src={qrCardImage} />
        <div className={styles.subtitle}>
          The six digits are on your Fi Nano QR card. You can also enter the full microchip number (9, 10 or 15 digits)
          from your vet paperwork.
        </div>
        <SerialNumberForm />
      </div>
    </div>
  );
}
